// publicRoutes.ts - the indexable, public URLs of sstk.studio.
//
// Derived from the same data the router renders from (docs pages, feature
// pillars, blog posts, legal documents), so a new page of any of those kinds
// needs no second list. This one list drives both the build's pre-render and
// sitemap.xml (scripts/prerender.mjs via src/entry-server.tsx), so the
// sitemap lists exactly the pages that exist as static HTML.
//
// Deliberately left out:
// - signed-in and auth routes (/app, /signin, /signup, /verify, /reset),
//   which robots.txt disallows;
// - /legal on its own, which renders the terms and so duplicates /legal/terms;
// - the docs redirect addresses in DOC_REDIRECTS;
// - pages keyed by live API data (/showcase/:slug, /play/:slug, /dev/:slug,
//   /wiki/:slug), which a static build cannot enumerate;
// - /wikis, the wiki directory, whose whole content is the live list of
//   published wikis: pre-rendered it would be a heading over an empty list.
//   It stays a client-rendered page.
import { DOC_PAGES } from '../docs/docsContent';
import { BLOG_POSTS } from '../marketing/screens-blog';
import { LEGAL_DOCS } from '../marketing/screens-company';
import { FEATURE_PILLAR_ORDER } from '../marketing/screens-feature-pages';

export const SITE_ORIGIN = 'https://sstk.studio';

/** Fixed marketing pages, in the order the router declares them. */
const MARKETING_PATHS = [
  '/',
  '/features',
  '/pricing',
  '/showcase',
  '/sunder',
  '/ninelives',
  '/furrow',
  '/dwindling',
  '/mewsli',
  '/blog',
  '/changelog',
  '/about',
  '/contact',
  '/attributions',
] as const;

/** Every public path, root first, without duplicates. */
export function publicPaths(): string[] {
  const paths = [
    ...MARKETING_PATHS,
    ...FEATURE_PILLAR_ORDER.map((pillar) => `/features/${pillar}`),
    ...BLOG_POSTS.map((post) => `/blog/post/${post.id}`),
    ...Object.keys(LEGAL_DOCS).map((doc) => `/legal/${doc}`),
    ...DOC_PAGES.map((page) => page.path),
  ];
  return [...new Set(paths)];
}

function escapeXml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** The sitemap.xml document for the given paths, with a shared lastmod date. */
export function sitemapXml(paths: string[], lastmod: string): string {
  const urls = paths
    .map((path) => `  <url>\n    <loc>${escapeXml(SITE_ORIGIN + path)}</loc>\n    <lastmod>${lastmod}</lastmod>\n  </url>`)
    .join('\n');
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls}\n</urlset>\n`;
}
